import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';

import useAuth from 'src/hooks/useAuth';
import useSystemAlertFetcher from 'src/hooks/useSystemAlertFetcher';

const STORAGE_KEY = 'system-alerts:last-known';

const SystemAlertContext = createContext();

// const defaultAlertsExample = [{ id: 'planExpiry', show: false, status: 'ok', severity: 'info' }];

const normalizeAlerts = (collectedAlerts) => {
  const now = Date.now();
  return collectedAlerts.map((alert) => ({
    id: alert.id || `${alert.sourceId}-${now}`,
    show: !!alert.show,
    severity: alert.severity || 'info',
    priority: alert.priority ?? 0,
    message: alert.message,
    title: alert.title,
    sourceId: alert.sourceId,
    lastUpdated: now,
    data: alert.data || alert,
    actionLabel: alert.actionLabel ?? undefined,
    onAction: alert.onAction ?? undefined
  }));
};

const initialState = {
  alerts: [],
  isLoading: false,
  error: null,
  lastUpdated: null,
  alertGeneration: 0 // Tracks distinct alert cycles (increments on clear/reset)
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_ALERTS':
      return { ...state, alerts: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_LAST_UPDATED':
      return { ...state, lastUpdated: action.payload };
    case 'INCREMENT_ALERT_GENERATION':
      return { ...state, alertGeneration: (state.alertGeneration || 0) + 1 };
    case 'RESET':
      return { ...initialState };
    default:
      return state;
  }
};

export const useSystemAlertContext = () => {
  const context = useContext(SystemAlertContext);
  if (!context) {
    throw new Error('useSystemAlertContext must be used within a SystemAlertProvider');
  }
  return context;
};

export const SystemAlertProvider = ({ children, sources = [], pollingEnabled = false }) => {
  const { isLoggedIn } = useAuth();

  const getInitial = () => {
    try {
      const cached = localStorage.getItem(STORAGE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) {
          return { ...initialState, alerts: parsed };
        }
      }
      return initialState;
    } catch (_) {
      return initialState;
    }
  };

  const [systemAlertState, systemAlertDispatch] = useReducer(reducer, undefined, getInitial);

  const persistAlerts = useCallback((alertsToPersist) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(alertsToPersist));
    } catch (_) {}
  }, []);

  const clearAlerts = useCallback(() => {
    systemAlertDispatch({ type: 'SET_ALERTS', payload: [] });
    systemAlertDispatch({ type: 'SET_ERROR', payload: null });
    systemAlertDispatch({ type: 'SET_LAST_UPDATED', payload: null });
    systemAlertDispatch({ type: 'INCREMENT_ALERT_GENERATION' });
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_) {}
  }, []);

  const handleFetchSuccess = useCallback(
    (collectedAlerts) => {
      const alerts = normalizeAlerts(collectedAlerts);
      systemAlertDispatch({ type: 'SET_ALERTS', payload: alerts });
      systemAlertDispatch({ type: 'SET_LAST_UPDATED', payload: Date.now() });
      systemAlertDispatch({ type: 'SET_ERROR', payload: null });
      persistAlerts(alerts);
    },
    [persistAlerts]
  );

  const handleFetchFailure = useCallback(() => {
    systemAlertDispatch({ type: 'SET_ERROR', payload: 'Failed to fetch alerts' });
  }, []);

  const handleLoading = useCallback((isLoading) => {
    systemAlertDispatch({ type: 'SET_LOADING', payload: isLoading });
  }, []);

  const { refreshAlerts } = useSystemAlertFetcher({
    sources,
    onFetchSuccess: handleFetchSuccess,
    onFetchFailure: handleFetchFailure,
    onLoading: handleLoading,
    pollingEnabled
  });

  useEffect(() => {
    if (!isLoggedIn) {
      clearAlerts();
    }
  }, [isLoggedIn, clearAlerts]);

  const value = useMemo(
    () => ({
      systemAlertState: {
        alerts: systemAlertState.alerts,
        isLoading: systemAlertState.isLoading,
        error: systemAlertState.error,
        lastUpdated: systemAlertState.lastUpdated,
        alertGeneration: systemAlertState.alertGeneration
      },
      systemAlertDispatch,
      refreshAlerts
    }),
    [systemAlertState, systemAlertDispatch, refreshAlerts]
  );

  return <SystemAlertContext.Provider value={value}>{children}</SystemAlertContext.Provider>;
};

export default SystemAlertContext;
